import { Package, Globe, Truck, SplitSquareHorizontal, HelpCircle, Coins } from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal, Eyebrow } from "@/components/Reveal";

const SHIPPING = [
  {
    icon: Globe,
    title: "Worldwide shipping",
    body: "We ship to most countries worldwide. Shipping rates, available destinations and delivery options are confirmed at checkout. Many eligible products ship free.",
  },
  {
    icon: Truck,
    title: "Delivery estimates",
    body: "Most current supplier shipping estimates are approximately 8–14 business days after dispatch. Delivery times are estimates only and may vary because of destination, customs, carrier delays, public holidays or other circumstances outside our control.",
  },
  {
    icon: Package,
    title: "Dispatch and tracking",
    body: "Once your order has been dispatched, tracking details will be sent by email where tracking is available.",
  },
  {
    icon: Coins,
    title: "International duties and taxes",
    body: "International orders may be subject to local import duties, taxes or customs charges depending on the destination. Any applicable charges are the customer's responsibility unless shown otherwise at checkout.",
  },
  {
    icon: SplitSquareHorizontal,
    title: "Split shipments",
    body: "Items in the same order may arrive separately where products are fulfilled from different locations.",
  },
  {
    icon: HelpCircle,
    title: "Questions",
    body: "For shipping questions, contact us through the Contact page.",
  },
];

const RETURNS = [
  {
    title: "30 day change of mind returns",
    body: "You may request a change of mind return within 30 days of delivery. Items must be unused, in their original condition and, where reasonably possible, in their original packaging. Contact us before returning an item so we can provide return instructions. Unless required by law, return shipping costs for change of mind returns are the customer's responsibility.",
  },
  {
    title: "Faulty, damaged or misdescribed goods",
    body: "If your item arrives faulty, damaged, unsafe or materially different from its description, contact us as soon as possible. Where possible, please contact us within 48 hours of delivery for transit damage and include photos of the item and packaging so we can resolve the issue quickly. This timeframe does not limit any rights you may have under applicable consumer law.",
  },
  {
    title: "Refunds",
    body: "Approved refunds are issued to the original payment method. Processing times after a refund is issued may vary depending on your bank or payment provider.",
  },
  {
    title: "Start a return",
    body: "Contact us through the Contact page with your order number and the reason for your return request.",
  },
];

export default function ShippingReturnsPage() {
  return (
    <main data-testid="shipping-returns-page" className="min-h-screen bg-void pt-28 sm:pt-32">
      <Seo
        title="Shipping & Returns — Weird O'Clock"
        siteName="Weird O'Clock"
        description="Worldwide shipping with delivery estimates of 8–14 business days after dispatch, plus 30 day change of mind returns at Weird O'Clock."
      />
      <div className="mx-auto max-w-[1100px] px-5 pb-24 sm:px-8">
        <Reveal>
          <Eyebrow>The practical bits</Eyebrow>
          <h1 className="font-display text-4xl font-black tracking-tight text-ivory sm:text-5xl lg:text-6xl">
            Shipping & <span className="font-editorial italic text-amber">returns</span>
          </h1>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-ivory/60">
            Everything you need to know about how your order gets to you —
            and how to send it back if it isn't weird enough. Good things are
            never quick.
          </p>
        </Reveal>

        <div className="mt-16">
          <Reveal>
            <h2 className="font-mono2 text-xs uppercase tracking-[0.3em] text-amber">01 — Shipping policy</h2>
          </Reveal>
          <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-2">
            {SHIPPING.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.06}>
                <div
                  data-testid={`shipping-card-${i}`}
                  className="h-full rounded-xl border border-white/10 bg-obsidian p-7 transition-colors hover:border-amber/40"
                >
                  <s.icon className="h-6 w-6 text-amber" />
                  <h3 className="mt-4 font-display text-lg font-bold text-ivory">{s.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-ivory/50">{s.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        <div className="mt-20">
          <Reveal>
            <h2 className="font-mono2 text-xs uppercase tracking-[0.3em] text-amber">02 — Refund policy</h2>
          </Reveal>
          <div className="mt-8 space-y-0">
            {RETURNS.map((r, i) => (
              <Reveal key={r.title} delay={i * 0.06}>
                <div
                  data-testid={`returns-item-${i}`}
                  className="grid grid-cols-12 gap-4 border-t border-white/10 py-8"
                >
                  <span className="col-span-2 font-mono2 text-sm text-amber sm:col-span-1">
                    /0{i + 1}
                  </span>
                  <h3 className="col-span-10 font-display text-lg font-bold text-ivory sm:col-span-4 sm:text-xl">
                    {r.title}
                  </h3>
                  <p className="col-span-10 col-start-3 max-w-lg text-sm leading-relaxed text-ivory/50 sm:col-span-6 sm:col-start-6">
                    {r.body}
                  </p>
                </div>
              </Reveal>
            ))}
            <div className="border-t border-white/10" />
          </div>
        </div>

        <Reveal className="mt-12 rounded-xl border border-white/10 bg-obsidian p-8">
          <p className="font-mono2 text-[10px] uppercase tracking-[0.3em] text-amber">
            Australian Consumer Law
          </p>
          <p className="mt-3 text-sm leading-relaxed text-ivory/60">
            Nothing in this policy excludes, restricts or modifies any rights
            or remedies you may have under the Australian Consumer Law or
            other applicable consumer protection laws.
          </p>
        </Reveal>

        <Reveal className="mt-8 rounded-xl border border-amber/30 bg-amber/5 p-8">
          <p className="font-editorial text-xl italic text-ivory/80">
            Still unsure about something?
          </p>
          <a
            href="/contact"
            data-testid="shipping-contact-link"
            className="mt-3 inline-block font-mono2 text-xs uppercase tracking-[0.2em] text-amber hover:underline"
          >
            Ask us directly →
          </a>
        </Reveal>
      </div>
    </main>
  );
}
